import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { CHERRY_DEV } from 'views/landingPages/CherrySoftware/cherryDevBrand';

/** Tres palabras bajo el logo, según el tipo de landing. */
export const MEDIA_LAB_TAGLINES = {
  producers: 'SHOWS · NICHOS · NETWORK',
  software: 'SISTEMAS · SOFTWARE · ESCALA',
  web: 'WEB · TIENDAS · VENTAS',
  hub: 'PRODUCCIÓN · TECH · COMUNIDAD',
  agency: 'ESTRATEGIA · MARCA · CRECIMIENTO',
};

/** Wordmark tipográfico: cherry (fino) + Dev (grueso), pegado. */
export const CherryDevWordmark = ({
  color = CHERRY_DEV.ink,
  fontSize = 'inherit',
  sx = {},
  ...props
}) => (
  <Box
    component="span"
    {...props}
    sx={{
      display: 'inline',
      whiteSpace: 'nowrap',
      letterSpacing: '-0.04em',
      color,
      fontSize,
      ...sx,
    }}
  >
    <Box component="span" sx={{ fontWeight: 300, letterSpacing: '-0.04em' }}>
      cherry
    </Box>
    <Box
      component="span"
      sx={{
        fontWeight: 900,
        letterSpacing: '-0.05em',
        marginLeft: '-0.02em',
      }}
    >
      Dev
    </Box>
  </Box>
);

CherryDevWordmark.propTypes = {
  color: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  sx: PropTypes.object,
};

const CherriesMark = ({ x = 0, y = 0, scale = 1 }) => (
  <g transform={`translate(${x}, ${y}) scale(${scale})`}>
    <path
      d="M20 14 C18 4 12 2 10 2 C14 6 14 12 16 16"
      stroke="#1B7A4E"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M20 14 C22 4 28 2 30 2 C26 6 26 12 24 16"
      stroke="#1B7A4E"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
    <path d="M16 6 C12 2 6 4 8 10 C12 10 16 8 16 6Z" fill="#1B7A4E" />
    <circle cx="14" cy="28" r="10" fill="#C41E3A" />
    <circle cx="11" cy="25" r="2.5" fill="#E85A6B" opacity="0.7" />
    <circle cx="26" cy="28" r="10" fill="#8B1528" />
    <circle cx="23" cy="25" r="2.2" fill="#C41E3A" opacity="0.75" />
  </g>
);

CherriesMark.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  scale: PropTypes.number,
};

/**
 * Wordmark con cerezas.
 * variant "tno" = TNO Media Lab; "cherry" = 🍒 cherryDev (cerezas antes del texto).
 */
const TnoMediaLabLogo = ({
  variant = 'tno',
  tagline = MEDIA_LAB_TAGLINES.agency,
  width,
  height,
  sx = {},
  alt,
}) => {
  const isCherry = variant === 'cherry';
  const label =
    alt ||
    (isCherry ? `cherryDev. ${tagline}` : `TNO Media Lab. ${tagline}`);
  const nameY = tagline ? 26 : 32;

  if (isCherry) {
    // Cerezas (antes) + cherry (fino) + Dev (grueso)
    const cherryScale = 1.05;
    const cherriesBottom = 38;
    // Baja un poco las cerezas respecto a la línea base del texto
    const cherriesY = nameY - cherriesBottom * cherryScale + 4;
    const cherriesW = 36 * cherryScale;
    const textX = cherriesW + 2;
    const viewTop = Math.min(0, cherriesY) - 1;
    const viewBottom = tagline ? nameY + 18 : nameY + 6;

    return (
      <Box
        component="svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 ${viewTop} 200 ${viewBottom - viewTop}`}
        role="img"
        aria-label={label}
        sx={{
          display: 'block',
          width: width || 'auto',
          height: height || 'auto',
          maxWidth: '100%',
          ...sx,
        }}
      >
        <title>{label}</title>
        <CherriesMark x={0} y={cherriesY} scale={cherryScale} />
        <text
          x={textX}
          y={nameY}
          fontFamily="'Outfit', 'Segoe UI', system-ui, -apple-system, sans-serif"
          fontSize="24"
          letterSpacing="-0.9"
          fill="#1C1214"
        >
          <tspan fontWeight="300">cherry</tspan>
          <tspan fontWeight="900" fontSize="25.5" dx="-0.6">
            Dev
          </tspan>
        </text>
        {tagline ? (
          <text
            x={textX}
            y={nameY + 15}
            fontFamily="'Outfit', 'Segoe UI', system-ui, -apple-system, sans-serif"
            fontSize="8.5"
            fontWeight="700"
            letterSpacing="1.3"
            fill="#1B7A4E"
          >
            {tagline}
          </text>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 300 48"
      role="img"
      aria-label={label}
      sx={{
        display: 'block',
        width: width || 'auto',
        height: height || 'auto',
        maxWidth: '100%',
        ...sx,
      }}
    >
      <title>{label}</title>
      <CherriesMark />
      <text
        x="48"
        y={tagline ? 22 : 31}
        fontFamily="'Segoe UI', system-ui, -apple-system, sans-serif"
        fontSize="18"
        fontWeight="800"
        letterSpacing="-0.5"
        fill="#1C1214"
      >
        TNO
      </text>
      <text
        x="92"
        y={tagline ? 22 : 31}
        fontFamily="'Segoe UI', system-ui, -apple-system, sans-serif"
        fontSize="18"
        fontWeight="600"
        letterSpacing="-0.3"
        fill="#C41E3A"
      >
        Media Lab
      </text>
      {tagline ? (
        <text
          x="48"
          y="38"
          fontFamily="'Segoe UI', system-ui, -apple-system, sans-serif"
          fontSize="8.5"
          fontWeight="700"
          letterSpacing="1.4"
          fill="#1B7A4E"
        >
          {tagline}
        </text>
      ) : null}
    </Box>
  );
};

TnoMediaLabLogo.propTypes = {
  variant: PropTypes.oneOf(['tno', 'cherry']),
  tagline: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
  sx: PropTypes.object,
  alt: PropTypes.string,
};

export default TnoMediaLabLogo;
